import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/BootstrapTextField";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import PageContainer from "components/PageContainer/PageContainer";
import { autocompleteDropdownStyles } from "Constants/Style";
import { useAutoMinimizeSidebar } from "CustomHooks/useAutoMinimizeSidebar";
import { useWarehouseSelection } from "CustomHooks/useWarehouseSelection";
import { useFormik } from "formik";
import { api } from "helpers/api_url";
import React, { useCallback, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchStockTransferById } from "slices/Warehouse/Dispatch/StockTransfer/thunk";
import { showConfirm } from "utils/swalHelper";
import * as Yup from "yup";
import StockTransferInwardTable from "../ConfigureItemsTables/StockTransferInwardTable";

interface GRNFormValues {
  dispatch_no: string;
  inward_date: string;
  transfer_from: string;
  transfer_from_id: string;
  transfer_to: string;
  transfer_to_id: string;
  upload_document: File | null;
  existing_document_file: string | null;
  remark: string;
  received_by?: string;
  received_by_user_id?: string;
}

interface ScannedItem {
  dispatch_item_id: string;
  item_id: string;
  inventory_item_id: string;
  bag_no: string;
  received_quantity: number;
  damaged_quantity?: number;
  selling_price?: number;
  remark?: string;
}

const StockTransferGRN: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const params = useParams<{ dispatch_id?: string }>();
  const dispatchId = params.dispatch_id;

  const [dispatchItems, setDispatchItems] = useState<any[]>([]);
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [userOptions, setUserOptions] = useState<any[]>([]);

  // Use custom hook for warehouse selection
  const { warehouseOptions, selectedWarehouseId, selectedWarehouse } =
    useWarehouseSelection({
      sessionStorageKey: "WarehouseIdDispatch",
      autoSelectFirst: true,
    });

  // Auto-minimize sidebar on tablet screens
  useAutoMinimizeSidebar();

  const user = useSelector((state: any) => state.Login.user);
  const currentUserName = user
    ? user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.username || ""
    : "";
  const currentUserId = user?.user_id || user?.id || "";

  // Get today's date in YYYY-MM-DD format for date input
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const validationSchema = Yup.object({
    dispatch_no: Yup.string().required("Dispatch number is required"),
    inward_date: Yup.string().required("Please select Inward Date"),
    transfer_from: Yup.string().required("Transfer from is required"),
    transfer_to: Yup.string().required("Transfer to is required"),
    remark: Yup.string(),
    upload_document: Yup.mixed().nullable(),
  });

  const formik = useFormik<GRNFormValues>({
    initialValues: {
      dispatch_no: "",
      inward_date: getTodayDate(),
      transfer_from: "",
      transfer_from_id: "",
      transfer_to: "",
      transfer_to_id: "",
      upload_document: null,
      existing_document_file: null,
      remark: "",
      received_by: currentUserName,
      received_by_user_id: currentUserId,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Validate scanned items
      // if (scannedItems.length === 0) {
      //   toast.error("Please scan at least one item");
      //   return;
      // }

      // Validate that all item bags are verified
      const unverifiedItems: string[] = [];
      let totalUnverifiedBags = 0;
      let totalBags = 0;

      dispatchItems.forEach((item) => {
        if (item.bags && item.bags.length > 0) {
          totalBags += item.bags.length;
          const unverifiedBags = item.bags.filter(
            (bag: any) => !bag || bag.status !== "verified"
          );
          if (unverifiedBags.length > 0) {
            totalUnverifiedBags += unverifiedBags.length;
            const itemName = item.item_name || item.item_code || "Item";
            unverifiedItems.push(
              `${itemName} (${unverifiedBags.length}/${item.bags.length} bags)`
            );
          }
        }
      });

      if (unverifiedItems.length > 0) {
        const itemsList =
          unverifiedItems.length > 3
            ? `${unverifiedItems.slice(0, 3).join(", ")} and ${
                unverifiedItems.length - 3
              } more...`
            : unverifiedItems.join(", ");
        toast.error(
          // `Please verify all bags before submitting. ${totalUnverifiedBags} of ${totalBags} bag(s) remaining: ${itemsList}`
          "Please scan all items before submitting the form."
        );
        console.log("Validation failed - Unverified bags:", {
          dispatchItems,
          unverifiedItems,
          totalUnverifiedBags,
          totalBags,
        });
        return;
      }

      // Build FormData
      const formData = new FormData();
      formData.append("dispatch_id", dispatchId || "");
      formData.append("inward_date", values.inward_date);
      formData.append("transfer_from_id", values.transfer_from_id || "");
      formData.append("transfer_to_id", values.transfer_to_id || "");
      formData.append("received_by", values.received_by || "");
      formData.append("received_by_user_id", values.received_by_user_id || "");
      formData.append("remarks", values.remark || "");

      if (values.upload_document && values.upload_document instanceof File) {
        formData.append("document_file", values.upload_document);
      }

      // Transform scanned items to grn_items format
      const grnItems = scannedItems.map((item) => ({
        dispatch_item_id: item.dispatch_item_id,
        item_id: item.item_id,
        received_quantity: item.received_quantity,
        damaged_quantity: item.damaged_quantity || 0,
        selling_price: item.selling_price || undefined,
        remark: item.remark || undefined,
        grn_items_details: [
          {
            quantity: item.received_quantity,
            selling_price: item.selling_price || undefined,
            remark: item.remark || undefined,
          },
        ],
      }));

      formData.append("grn_items", JSON.stringify(grnItems));

      try {
        await Axios.post(api.warehouse2.pendingStockTransfer.create, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        toast.success("Stock transfer inward (GRN) created successfully");
        navigate("/warehouse-item-inward");
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to create stock transfer inward";
        toast.error(errorMessage);
        console.error("Error creating stock transfer inward:", err);
      }
    },
  });

  const { setFieldValue } = formik;
  const transferToIdValue = formik.values.transfer_to_id;

  // Fetch dispatch data
  useEffect(() => {
    const fetchDispatchData = async () => {
      if (!dispatchId || initialDataLoaded) {
        return;
      }

      setIsLoadingInitialData(true);
      try {
        const dispatchDataResult = await dispatch(
          fetchStockTransferById(dispatchId)
        ).unwrap();

        if (dispatchDataResult) {
          // Prefill form fields with dispatch data
          setFieldValue(
            "dispatch_no",
            dispatchDataResult.dispatch_number || ""
          );
          setFieldValue(
            "transfer_from",
            dispatchDataResult.transfer_from_warehouse?.warehouse_name || ""
          );
          setFieldValue(
            "transfer_from_id",
            dispatchDataResult.transfer_from_warehouse?.warehouse_id || ""
          );
          setFieldValue(
            "transfer_to",
            dispatchDataResult.transfer_to_warehouse?.warehouse_name || ""
          );
          setFieldValue(
            "transfer_to_id",
            dispatchDataResult.transfer_to_warehouse?.warehouse_id || ""
          );

          // Set dispatch items
          if (dispatchDataResult.dispatch_items) {
            const items = dispatchDataResult.dispatch_items.map(
              (item: any) => ({
                ...item,
                bags: (item.bags || []).map((b: any) => ({
                  ...b,
                  status: "not_verified",
                })),
              })
            );

            setDispatchItems(items);
          }

          setInitialDataLoaded(true);
        } else {
          toast.error("Dispatch data not found");
          navigate("/warehouse-item-inward");
          setIsLoadingInitialData(false);
        }
      } catch (error: any) {
        console.error("Error fetching dispatch data:", error);
        toast.error(error?.message || "Failed to load dispatch data");
        navigate("/warehouse-item-inward");
      } finally {
        setIsLoadingInitialData(false);
      }
    };

    if (dispatchId && !initialDataLoaded) {
      fetchDispatchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchId, initialDataLoaded, dispatch, navigate, setFieldValue]);

  // Fetch user options for Received By field
  useEffect(() => {
    const fetchUserOptions = async () => {
      try {
        const response = await Axios.get("/users/dropdown/all");
        setUserOptions(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching user options:", error);
        setUserOptions([]);
      }
    };
    fetchUserOptions();
  }, []);

  // Auto-select transfer_to warehouse (only if not already set from dispatch data)
  useEffect(() => {
    if (
      warehouseOptions.length > 0 &&
      !transferToIdValue &&
      initialDataLoaded
    ) {
      const currentWarehouseId =
        selectedWarehouseId || sessionStorage.getItem("WarehouseIdDispatch");
      const currentWarehouse = warehouseOptions.find(
        (w: any) => String(w.warehouse_id) === String(currentWarehouseId)
      );

      if (currentWarehouse) {
        setFieldValue("transfer_to", currentWarehouse.warehouse_name);
        setFieldValue("transfer_to_id", currentWarehouse.warehouse_id);
      }
    }
  }, [
    warehouseOptions,
    selectedWarehouseId,
    initialDataLoaded,
    transferToIdValue,
    setFieldValue,
  ]);

  const handleItemsScanned = useCallback((items: ScannedItem[]) => {
    setScannedItems(items);
  }, []);

  return (
    <PageContainer>
      <FormHeader
        title="Create GRN (Stock Transfer)"
        firstLabel="Stock Transfer"
        secondLabel="Create GRN"
        handleArrowClick={() => navigate("/warehouse-item-inward")}
      />

      {isLoadingInitialData ? (
        <LoadingSpinner />
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>
                  Dispatch No. <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="dispatch_no"
                  value={formik.values.dispatch_no}
                  disabled
                  style={{ backgroundColor: "#f5f5f5" }}
                />
              </div>
            </Col>

            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>
                  Inward Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="inward_date"
                  value={formik.values.inward_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.inward_date && formik.touched.inward_date
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.inward_date}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>
                  Transfer From <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="transfer_from"
                  value={formik.values.transfer_from}
                  disabled
                  style={{ backgroundColor: "#f5f5f5" }}
                />
              </div>
            </Col>

            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>
                  Transfer To <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={warehouseOptions}
                  getOptionLabel={(option: any) => `${option?.warehouse_name || ""}${option?.warehouse_type ? ` (${option.warehouse_type})` : ""}`}
                  value={
                    warehouseOptions.find(
                      (opt: any) =>
                        opt.warehouse_id === formik.values.transfer_to_id
                    ) || null
                  }
                  onChange={(event, selectedOption: any) => {
                    formik.setFieldValue(
                      "transfer_to_id",
                      selectedOption?.warehouse_id || ""
                    );
                    formik.setFieldValue(
                      "transfer_to",
                      selectedOption?.warehouse_name || ""
                    );
                    // Update sessionStorage
                    if (selectedOption?.warehouse_id) {
                      sessionStorage.setItem(
                        "WarehouseIdDispatch",
                        String(selectedOption.warehouse_id)
                      );
                    }
                    if (selectedOption?.warehouse_name) {
                      formik.setFieldError("transfer_to", undefined);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("transfer_to", true)}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Warehouse"
                      name="transfer_to"
                      error={
                        formik.touched.transfer_to &&
                        Boolean(formik.errors.transfer_to)
                      }
                      helperText={
                        formik.touched.transfer_to && formik.errors.transfer_to
                      }
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) => opt?.warehouse_name || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>

            {/* Received By */}
            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>Received By</Form.Label>
                <Autocomplete
                  options={userOptions}
                  getOptionLabel={(option: any) =>
                    option?.first_name && option?.last_name
                      ? `${option.first_name} ${option.last_name}`
                      : option?.username || option?.email || ""
                  }
                  value={
                    userOptions.find(
                      (opt: any) =>
                        opt.user_id === formik.values.received_by_user_id
                    ) || null
                  }
                  onChange={(event, selectedOption: any) => {
                    formik.setFieldValue(
                      "received_by_user_id",
                      selectedOption?.user_id || ""
                    );
                    formik.setFieldValue(
                      "received_by",
                      selectedOption?.first_name && selectedOption?.last_name
                        ? `${selectedOption.first_name} ${selectedOption.last_name}`
                        : selectedOption?.username || ""
                    );
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select User"
                      name="received_by"
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        opt?.first_name && opt?.last_name
                          ? `${opt.first_name} ${opt.last_name}`
                          : opt?.username || opt?.email || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>

            {/* Remarks */}
            <Col lg={3} md={4}>
              <div className="mb-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  type="text"
                  name="remark"
                  placeholder="Enter Remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>

            {/* Document */}
            {/* Document */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Upload document
                  {/* <span className="text-danger">*</span> */}
                </Form.Label>

                {/* Custom styled box */}
                <div
                  className={`d-flex align-items-center justify-content-between p-2 border rounded ${
                    formik.errors.upload_document &&
                    formik.touched.upload_document
                      ? "border-danger"
                      : ""
                  }`}
                  style={{
                    height: "40px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: "#fff",
                    borderColor:
                      formik.errors.upload_document &&
                      formik.touched.upload_document
                        ? "#dc3545"
                        : "#ced4da",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#80bdff";
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#ced4da";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                  onClick={() => {
                    document.getElementById("uploadDocInput")?.click();
                  }}
                >
                  <span
                    className="text-muted"
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginRight: "8px",
                      minWidth: 0,
                    }}
                    title={
                      formik.values.upload_document instanceof File
                        ? formik.values.upload_document.name
                        : formik.values.existing_document_file
                        ? formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        : "Click to upload document"
                    }
                  >
                    {formik.values.upload_document instanceof File ? (
                      formik.values.upload_document.name
                    ) : formik.values.existing_document_file ? (
                      <a
                        href={formik.values.existing_document_file}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-decoration-none text-primary"
                        style={{
                          cursor: "pointer",
                          display: "inline-block",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={
                          formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        }
                      >
                        {formik.values.existing_document_file
                          .split("/")
                          .pop() || "Existing Document"}
                      </a>
                    ) : (
                      "Click to upload document"
                    )}
                  </span>

                  {/* Action buttons */}
                  <div
                    className="d-flex align-items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                    style={{ flexShrink: 0 }}
                  >
                    {formik.values.upload_document instanceof File ? (
                      <>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("uploadDocInput")?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue(
                              "existing_document_file",
                              null
                            );
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : formik.values.existing_document_file ? (
                      <>
                        <a
                          href={formik.values.existing_document_file}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="View document"
                        >
                          <i
                            className="bi bi-eye text-primary"
                            style={{
                              cursor: "pointer",
                              fontSize: "1rem",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          ></i>
                        </a>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("uploadDocInput")?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue(
                              "existing_document_file",
                              null
                            );
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : (
                      <i
                        className="bi bi-upload"
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          color: "#6c757d",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#007bff";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#6c757d";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                    )}
                  </div>
                </div>

                {/* Hidden real file input */}
                <Form.Control
                  id="uploadDocInput"
                  type="file"
                  name="upload_document"
                  style={{ display: "none" }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const file = event.currentTarget.files?.[0] || null;
                    formik.setFieldValue("upload_document", file);
                    // Clear existing_document_file when a new file is uploaded
                    if (file) {
                      formik.setFieldValue("existing_document_file", null);
                    }
                    formik.setFieldTouched("upload_document", true);
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.upload_document &&
                    formik.touched.upload_document
                  }
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.upload_document}
                </Form.Control.Feedback>
              </div>
            </Col>
          </Row>

          <hr />

          <StockTransferInwardTable
            dispatchItems={dispatchItems}
            onItemsScanned={handleItemsScanned}
            onDispatchItemsUpdate={(updatedItems) => {
              setDispatchItems(updatedItems);
            }}
          />

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton
              label="Cancel"
              onClick={() => {
                showConfirm(
                  "Any unsaved changes will be lost.",
                  "Are you sure?",
                  "Yes",
                  "No"
                ).then((result) => {
                  if (result.isConfirmed) {
                    navigate("/warehouse-item-inward");
                  }
                });
              }}
            />

            <SubmitButton
              isSubmitting={formik.isSubmitting}
              labels={{
                create: "Create GRN",
                submitting: "Creating GRN",
              }}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default StockTransferGRN;
